import { useEffect, useRef, useState } from 'react'
import {
  FormLayout,
  TextInput,
  Dropdown,
  Spinner,
  Button,
} from 'components/common'
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form'
import toast from 'react-hot-toast'
import { FOC_FORM_FIELDS } from './utils'
import { getBarcodePrice } from 'utils/api/focApi'
import { fetchStores } from 'utils/api/storeApi'
import { roundToFourDecimals } from 'utils/helpers/round'
import { handleError } from 'utils/helpers/handleError'

interface StoreDetailsFormProps {
  register: UseFormRegister<any>
  control: Control<any>
  errors: FieldErrors<any>
  setValue: UseFormSetValue<any>
}

const {
  APPLIES_TO_MAGENTO_ORDER_NO,
  LOCATION_CODE,
  BARCODE_NO,
  LINE_AMOUNT_INCL_VAT,
  VAT_AMOUNT,
  LINE_AMOUNT_EXCL_VAT,
  ORDER_TYPE,
  QUANTITY,
} = FOC_FORM_FIELDS

const FocOrderForm = ({
  register,
  control,
  errors,
  setValue,
}: StoreDetailsFormProps) => {
  /* -------------------- Local State -------------------- */
  // const [isManualOverride, setIsManualOverride] = useState(false)
  // const [isFetchingStore, setIsFetchingStore] = useState(false)
  const [isFetchingPrice, setIsFetchingPrice] = useState(false)
  const [storeOptions, setStoreOptions] = useState<any[]>([])
  const [loadingStores, setLoadingStores] = useState(true)
  const [activeStore, setActiveStore] = useState('')
  const previousBarcodeRef = useRef<string | null>(null)

  const orderType = useWatch({
    control,
    name: ORDER_TYPE.key,
    defaultValue: 'MAGENTO',
  })
  // const orderNo = useWatch({ control, name: APPLIES_TO_MAGENTO_ORDER_NO.key })
  const barcodeNo = useWatch({ control, name: BARCODE_NO.key })
  const storeWatch = useWatch({
    control,
    name: LOCATION_CODE.key,
    defaultValue: activeStore,
  })
  const location = useWatch({ control, name: LOCATION_CODE.key })

  useEffect(() => {
    if (!location) return
    previousBarcodeRef.current = ''
    setValue(BARCODE_NO.key, '')
    setValue(LINE_AMOUNT_EXCL_VAT.key, '')
    setValue(LINE_AMOUNT_INCL_VAT.key, '')
    setValue(VAT_AMOUNT.key, '')
  }, [location])

  // Determine if location dropdown should be disabled
  // const isLocationDisabled = orderType === 'MAGENTO' && !isManualOverride

  /* -------------------- Data Fetching -------------------- */
  useEffect(() => {
    const getStores = async () => {
      try {
        const { data } = await fetchStores({
          limit: 500,
          page: 1,
          isActive: true,
          isThirdPartyStore: false,
        })
        const stores =
          data?.docs?.map((s: any) => ({
            label: s.storeName,
            value: s._id,
          })) || []

        setStoreOptions([{ label: '--NONE--', value: '' }, ...stores])
      } catch (error) {
        setStoreOptions([])
      } finally {
        setLoadingStores(false)
      }
    }
    getStores()
  }, [])

  // Reset logic when Order Type switches
  useEffect(() => {
    // setIsManualOverride(orderType !== 'MAGENTO')

    // Clear location when switching to start fresh
    setValue(LOCATION_CODE.key, '')
    setActiveStore('')
  }, [orderType, setValue])

  useEffect(() => {
    if (activeStore) {
      const matchedStore = storeOptions.find((opt) => opt.value === activeStore)

      if (matchedStore) {
        setValue(LOCATION_CODE.key, matchedStore.value, {
          shouldValidate: true,
        })
      }
    }
  }, [activeStore])

  /* -------------------- Handlers -------------------- */
  // const handleFetchMagentoStore = async () => {
  //   if (!orderNo || orderNo.trim().length < 3) return

  //   setIsFetchingStore(true)
  //   try {
  //     const response = await getOrderStoreCode({ orderNo })
  //     const storeCode = response?.data?.storeCode

  //     if (!storeCode) {
  //       toast.error('Store code not found. Please select manually.')
  //       setIsManualOverride(true)
  //       setActiveStore('')
  //       return
  //     }

  //     const matchedStore = storeOptions.find((opt) => opt.value === storeCode)

  //     if (!matchedStore) {
  //       toast.error(
  //         `Store found in Magento but not present in system with code "${storeCode}". Please contact to admin.`,
  //         { duration: 3000 },
  //       )
  //       setActiveStore('')
  //       return
  //     }

  //     const finalValue = matchedStore ? matchedStore.value : storeCode

  //     setActiveStore(finalValue)
  //     setIsManualOverride(false)
  //     toast.success(`Store "${storeCode}" selected successfully`)
  //   } catch (error) {
  //     handleError(error, '', { default: 'Failed to fetch store from Magento' })
  //     setIsManualOverride(true)
  //   } finally {
  //     setIsFetchingStore(false)
  //   }
  // }

  const handleFetchBarcodePrice = async () => {
    if (
      !barcodeNo ||
      barcodeNo.trim().length < 3 ||
      isFetchingPrice ||
      !storeWatch
    ) {
      return toast.error('Please ensure Barcode and Location are provided')
    }

    setIsFetchingPrice(true)
    const toastId = toast.loading('Fetching price...', {
      duration: Infinity,
    })
    try {
      const response = await getBarcodePrice({
        barcode: barcodeNo,
        storeCode: storeWatch,
      })
      const pricing = response?.data

      const pricingNotFound =
        !pricing ||
        pricing?.unitPriceInclVat === null ||
        pricing?.amountExclVat === null

      if (pricingNotFound) {
        toast.error('Price not found, Please check Barcode and location.', {
          duration: 3000,
          id: toastId,
        })
        return
      }

      setValue(
        LINE_AMOUNT_EXCL_VAT.key,
        roundToFourDecimals(pricing?.amountExclVat),
        { shouldValidate: true },
      )
      setValue(LINE_AMOUNT_INCL_VAT.key, pricing?.unitPriceInclVat, {
        shouldValidate: true,
      })
      setValue(VAT_AMOUNT.key, pricing?.vatAmount, { shouldValidate: true })

      toast.success('Price fetched successfully', {
        duration: 3000,
        id: toastId,
      })
    } catch (error) {
      handleError(error, toastId, {
        default: 'Pricing not found for this combination',
      })

      setValue(LINE_AMOUNT_EXCL_VAT.key, '')
      setValue(LINE_AMOUNT_INCL_VAT.key, '')
      setValue(VAT_AMOUNT.key, '')
    } finally {
      setIsFetchingPrice(false)
    }
  }

  if (loadingStores) {
    return <Spinner />
  }

  return (
    <>
      <h3 className="title mb-4">Order Details</h3>
      <FormLayout>
        <div className="grid gap-5 md:grid-cols-2">
          <Dropdown
            label="Order Type"
            setValue={setValue}
            name={ORDER_TYPE.key}
            defaultValue={'MAGENTO'}
            required
            options={[
              { label: 'Aggregator Order', value: 'AGGREGATOR' },
              { label: 'Magento Order', value: 'MAGENTO' },
            ]}
            error={errors?.[ORDER_TYPE.key]?.message as string}
          />

          <TextInput
            label={APPLIES_TO_MAGENTO_ORDER_NO.label}
            name={APPLIES_TO_MAGENTO_ORDER_NO.key}
            register={register}
            required
            error={errors?.[APPLIES_TO_MAGENTO_ORDER_NO.key]?.message as string}
            disabled={isFetchingPrice}
          />

          <Dropdown
            label="Location"
            name={LOCATION_CODE.key}
            setValue={setValue}
            required
            defaultValue={activeStore}
            options={storeOptions}
            error={errors?.[LOCATION_CODE.key]?.message as string}
            hintText={'Select store of the order'}
          />

          {/* ---------------- Fetch Button (Magento only) ---------------- */}
          {/* {orderType === 'MAGENTO' && (
            <div className="flex items-center">
              <Button
                type="button"
                onClick={handleFetchMagentoStore}
                disabled={
                  isFetchingStore || !orderNo || orderNo.trim().length < 3
                }
                buttonText={
                  isFetchingStore ? 'Fetching Store...' : 'Fetch Magento Store'
                }
              />
            </div>
          )} */}
        </div>
      </FormLayout>

      <h3 className="title">Order Items</h3>

      <FormLayout>
        <div className="grid gap-5 md:grid-cols-2">
          <TextInput
            label={BARCODE_NO.label}
            name={BARCODE_NO.key}
            register={register}
            required
            onBlur={(e) => {
              const value = e.target.value?.trim()

              if (
                !isFetchingPrice &&
                value &&
                value.length >= 3 &&
                value !== previousBarcodeRef.current
              ) {
                previousBarcodeRef.current = value
                handleFetchBarcodePrice()
              }
            }}
            disabled={isFetchingPrice}
            error={errors?.[BARCODE_NO.key]?.message as string}
          />

          {/* <div className="mb-1 flex items-end">
            <Button
              type="button"
              onClick={() => handleFetchBarcodePrice()}
              disabled={
                isFetchingPrice || !barcodeNo || barcodeNo.trim().length < 3
              }
              buttonText={
                isFetchingPrice ? 'Fetching price...' : 'Fetch Barcode price'
              }
            />
          </div> */}

          <TextInput
            label={QUANTITY.label}
            name={QUANTITY.key}
            register={register}
            defaultValue={1}
            required
            error={errors?.[QUANTITY.key]?.message as string}
            disabled={isFetchingPrice}
          />

          <TextInput
            label={LINE_AMOUNT_INCL_VAT.label}
            name={LINE_AMOUNT_INCL_VAT.key}
            register={register}
            required
            error={errors?.[LINE_AMOUNT_INCL_VAT.key]?.message as string}
            disabled
            readOnly
          />
          <TextInput
            label={VAT_AMOUNT.label}
            name={VAT_AMOUNT.key}
            register={register}
            required
            error={errors?.[VAT_AMOUNT.key]?.message as string}
            disabled
            readOnly
          />
          <TextInput
            label={LINE_AMOUNT_EXCL_VAT.label}
            name={LINE_AMOUNT_EXCL_VAT.key}
            register={register}
            required
            error={errors?.[LINE_AMOUNT_EXCL_VAT.key]?.message as string}
            disabled
            readOnly
          />
        </div>
      </FormLayout>
      <Button
        buttonText="Create FOC Order"
        className="mt-7 w-44"
        type="submit"
      />
    </>
  )
}

export default FocOrderForm
